import type { ReactNode } from "react";
import { formatLocalDate } from "../../../shared/local-date";
import {
  METRIC_DENOMINATOR_LABELS,
  METRIC_SOURCE_LABELS,
  metricReasonText,
  type FunnelBandStep,
  type FunnelStep,
  type FunnelStepStatus,
  type FunnelTarget,
  type MetricBand,
  type MetricChip,
  type MetricEnvelope,
  type MetricName,
  type MetricPeriod,
  type MetricSource,
  type MetricUnit,
  type RdTenure,
  type SessionFunnel,
  type TenureBand,
} from "../../../shared/metrics";

export type MetricCadence = "weekly" | "monthly";
export interface MetricSection {
  id: string;
  title: string;
  cadence: MetricCadence;
  metrics: MetricName[];
}
export interface PeriodColumn {
  label: string;
  value: string;
}
export type MetricShape = Pick<MetricEnvelope, "unit" | "per">;
export interface PopoverFact {
  term: string;
  detail: string;
}

export const METRIC_SECTIONS: readonly MetricSection[] = [
  {
    id: "utilization",
    title: "Utilization and hours",
    cadence: "weekly",
    metrics: ["billableHours", "clinicalHours", "billableUtilization", "forwardLoad"],
  },
  {
    id: "retention",
    title: "Retention and continuity",
    cadence: "monthly",
    metrics: ["retention", "ownedReferrals"],
  },
  {
    id: "cadence",
    title: "Cadence and cancellations",
    cadence: "monthly",
    metrics: ["visitCadence", "recurringBookings", "cancellationRate"],
  },
  {
    id: "new-patients",
    title: "New patients and visit depth",
    cadence: "monthly",
    metrics: ["newReferrals"],
  },
];
export const METRIC_LABELS: Record<MetricName, string> = {
  billableHours: "Billable hours",
  clinicalHours: "Clinical hours",
  billableUtilization: "Billable utilization",
  forwardLoad: "Forward load",
  retention: "Retention",
  ownedReferrals: "Owned referrals",
  visitCadence: "Visit cadence",
  recurringBookings: "Recurring bookings",
  cancellationRate: "Cancellation rate",
  newReferrals: "New referrals",
};
export const CHIP_VARIANTS: Record<
  MetricChip,
  "success" | "neutral" | "warning" | "danger" | "muted"
> = {
  Above: "success",
  Meets: "neutral",
  Watch: "warning",
  Below: "danger",
  "No target": "muted",
  "Too early": "muted",
};
export const FUNNEL_BAR_CLASSES: Record<FunnelStepStatus, string> = {
  Meets: "bg-success-soft",
  Watch: "bg-warning-soft",
  Below: "bg-danger-soft",
  "No target": "bg-secondary",
};
export const FUNNEL_DOT_CLASSES: Record<FunnelStepStatus, string | null> = {
  Meets: "bg-success",
  Watch: "bg-warning",
  Below: "bg-danger",
  "No target": null,
};
export const ILLUSTRATIVE_NOTE = "Illustrative fake data until real sources land.";
export const EMPTY_VALUE = "—";
const UNIT_WORDS: Record<MetricUnit, string> = {
  hours: "hrs",
  percent: "%",
  visits: "visits",
  referrals: "referrals",
};

export function dataAnchor(dataThrough: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dataThrough)) return dataThrough;

  return new Date(dataThrough).toISOString().slice(0, 10);
}

export function formatMetricValue(value: number | null, unit: MetricUnit): string {
  if (value === null) return EMPTY_VALUE;

  if (unit === "percent") return `${Math.round(value)}%`;

  if (unit === "hours") return value.toFixed(1);

  if (unit === "visits") return value.toFixed(2);

  return String(Math.round(value));
}

export function metricUnitLabel(envelope: MetricShape): string {
  const denominator = METRIC_DENOMINATOR_LABELS[envelope.per].long;
  const word = UNIT_WORDS[envelope.unit];

  return denominator ? `${word} ${denominator}` : word;
}

function metricTargetText(envelope: MetricEnvelope): string {
  return envelope.target ? envelope.target.label : "No target";
}

/**
 * A dash states why it is a dash: a null value with a reason reads the reason,
 * so a data gap never disguises itself as "No target" (NO-667).
 */
export function metricSublabel(envelope: MetricEnvelope): string {
  if (envelope.value === null && envelope.reason !== null)
    return metricReasonText(envelope.reason) ?? metricTargetText(envelope);

  return metricTargetText(envelope);
}

/** The server says the minimum is missing; the client only renders the flag. */
export const MISSING_MINIMUM_FLAG = "No contract minimum on file · ";
export const MISSING_MINIMUM_LINK_LABEL = "set it in the Functional form";

/**
 * Renders the Functional form link around its label. Routes supply it, because
 * the link target is a route concern; without one the label is plain text.
 */
export type FunctionalFormLink = (label: ReactNode) => ReactNode;

export function showsMissingMinimumFlag(
  envelope: Pick<MetricEnvelope, "targetBasis">
): boolean {
  return envelope.targetBasis === "company_floor";
}

export function formatBand(band: MetricBand, unit: MetricUnit): string {
  const min = band.min === null ? null : formatMetricValue(band.min, unit);
  const max = band.max === null ? null : formatMetricValue(band.max, unit);
  if (min !== null && max !== null) return `${min}–${max}`;

  if (min !== null) return `≥ ${min}`;

  return max === null ? EMPTY_VALUE : `≤ ${max}`;
}

export function formatFleet(
  fleet: MetricEnvelope["fleet"],
  unit: MetricUnit
): string | null {
  if (!fleet) return null;

  const parts: string[] = [];
  if (fleet.median !== null)
    parts.push(`median ${formatMetricValue(fleet.median, unit)}`);

  if (fleet.p75 !== null) parts.push(`p75 ${formatMetricValue(fleet.p75, unit)}`);

  return parts.length === 0
    ? null
    : `${parts.join(" · ")} · ${fleet.label} (${fleet.rdCount} RDs)`;
}

export function popoverFacts(envelope: MetricEnvelope): PopoverFact[] {
  const facts: PopoverFact[] = [
    { term: "Unit", detail: metricUnitLabel(envelope) },
    { term: "Window", detail: envelope.window.label },
    { term: "Target", detail: metricTargetText(envelope) },
    { term: "Source", detail: METRIC_SOURCE_LABELS[envelope.source] },
    { term: "Data through", detail: formatLocalDate(dataAnchor(envelope.dataThrough)) },
  ];
  if (envelope.watch)
    facts.push({ term: "Watch", detail: formatBand(envelope.watch, envelope.unit) });

  const fleet = formatFleet(envelope.fleet, envelope.unit);
  if (fleet) facts.push({ term: "Fleet", detail: fleet });

  const periods = envelope.periods ?? [];
  facts.push(...reasonFacts(envelope.reason, periods), ...partialFacts(periods));

  return facts;
}

interface PeriodCellReasonGroup {
  text: string;
  labels: string[];
}

function nullPeriodCellReasonGroups(
  periods: readonly MetricPeriod[]
): PeriodCellReasonGroup[] {
  const groups = new Map<string, string[]>();

  for (const period of periods) {
    const text = metricReasonText(period.reason);
    if (period.value !== null || text === null) continue;

    groups.set(text, [...(groups.get(text) ?? []), period.label]);
  }

  return [...groups].map(([text, labels]) => ({ text, labels }));
}

function reasonGroupDetail(group: PeriodCellReasonGroup, periodCount: number): string {
  return group.labels.length === periodCount
    ? group.text
    : `${group.labels.join(", ")}: ${group.text}`;
}

/** An envelope reason repeated by a Period Cell group is said once, in the group's form. */
function reasonFacts(
  reason: MetricEnvelope["reason"],
  periods: readonly MetricPeriod[]
): PopoverFact[] {
  const groups = nullPeriodCellReasonGroups(periods);
  const envelopeText = metricReasonText(reason);
  const repeated = groups.some((group) => group.text === envelopeText);
  const details = groups.map((group) => reasonGroupDetail(group, periods.length));
  if (envelopeText !== null && !repeated) details.unshift(envelopeText);

  return details.map((detail) => ({ term: "Reason", detail }));
}

/** A partial null Period Cell is already explained by its Reason group. */
function partialFacts(periods: readonly MetricPeriod[]): PopoverFact[] {
  const labels = periods
    .filter((period) => period.partial && period.value !== null)
    .map((period) => period.label);

  return labels.length === 0 ? [] : [{ term: "Partial", detail: labels.join(", ") }];
}

/** The producer supplies each period's arithmetic and label. */
export function periodColumns(envelope: MetricEnvelope): PeriodColumn[] {
  return (envelope.periods ?? []).map((period) => ({
    label: period.label,
    value: formatMetricValue(period.value, envelope.unit),
  }));
}

const CADENCE_LABELS: Record<MetricCadence, string> = {
  weekly: "Weekly",
  monthly: "Monthly",
};

function latestDataAnchor(envelopes: readonly MetricEnvelope[]): string | null {
  const anchors = envelopes.map((envelope) => dataAnchor(envelope.dataThrough)).sort();

  return anchors[anchors.length - 1] ?? null;
}

function cellSources(envelope: MetricEnvelope): MetricSource[] {
  const periods = envelope.periods ?? [];

  return periods.length === 0
    ? [envelope.source]
    : periods.map((period) => period.source);
}

/** The section is as fresh as its latest metric, so the header date is the latest `dataThrough`. */
export function sectionHeaderLine(
  section: MetricSection,
  envelopes: readonly MetricEnvelope[]
): string {
  const latest = latestDataAnchor(envelopes);
  const sources = new Set(envelopes.flatMap(cellSources));
  const parts = [CADENCE_LABELS[section.cadence]];
  if (latest !== null) parts.push(`through ${formatLocalDate(latest)}`);

  for (const source of sources) parts.push(METRIC_SOURCE_LABELS[source]);

  return parts.join(" · ");
}

export function dataThroughSentence(
  anchor: string,
  sources: readonly MetricSource[]
): string {
  const labels = [...new Set(sources)].map((source) => METRIC_SOURCE_LABELS[source]);

  return `Values through ${formatLocalDate(anchor)} · ${labels.join(" · ")}`;
}

export function pageDescription(
  anchor: string,
  sources: readonly MetricSource[]
): string {
  return dataThroughSentence(anchor, sources);
}

export function bandLabel(band: TenureBand): string {
  return band === "0-90" ? "0–90 days" : "90+ days";
}

export function tenureLabel(tenure: RdTenure, viewing: TenureBand): string {
  const suffix = viewing === tenure.band ? "" : " (viewing)";

  return `Targets for ${bandLabel(viewing)}${suffix} · day ${tenure.daysAsOf} of tenure`;
}

export function cohortSubtitle(cohort: SessionFunnel["cohort"]): string {
  const start = formatLocalDate(cohort.firstVisitStart);
  const end = formatLocalDate(cohort.firstVisitEnd);

  return `First visits ${start} – ${end} · ${cohort.patients} patients · followed ${cohort.followedDays} days`;
}

export interface FunnelStepView {
  id: FunnelStep["id"];
  label: string;
  sublabel: string | null;
  patients: number;
  sharePercent: number;
  medicareSharePercent: number;
  status: FunnelStepStatus;
}
export interface FunnelGapView {
  afterStepId: FunnelStep["id"];
  dropOffPercent: number;
  targetText: string;
  status: FunnelStepStatus;
}
export interface FunnelView {
  steps: FunnelStepView[];
  gaps: FunnelGapView[];
  medicareCapped: number;
}

function percentOf(part: number, whole: number): number {
  if (whole <= 0) return 0;

  return Math.round((part / whole) * 100);
}

export function funnelTargetText(target: FunnelTarget): string {
  if (target.kind === "maxDropOff") return `target ${target.value}% or less`;

  if (target.kind === "reach") return `reach ${target.value}% or more`;

  if (target.kind === "medicareCapExcluded")
    return "no target, Medicare cap patients excluded";

  return "no target";
}

function reachSublabel(bandStep: FunnelBandStep): string | null {
  const range = bandStep.shouldReach;

  if (range) {
    const span = range.lo === range.hi ? `${range.lo}%` : `${range.lo}–${range.hi}%`;

    return `${span} should reach`;
  }

  if (bandStep.target.kind === "reach") return `${bandStep.target.value}% should reach`;

  return null;
}

function stepSublabel(
  step: FunnelStep,
  bandStep: FunnelBandStep,
  nonMedicare: number
): string | null {
  if (step.id === "s1") return `${step.patients} first visits`;

  if (step.id === "s4_5") return `of ${nonMedicare} non-Medicare`;

  if (step.id === "s12plus") return "“Lifers”";

  return reachSublabel(bandStep);
}

function medicareCappedOf(steps: readonly FunnelStep[]): number {
  return steps.find((step) => step.id === "s3")?.medicareCapped ?? 0;
}

/** Shares are of first visits; steps after Session 3 drop off against the non-Medicare base. */
export function funnelView(funnel: SessionFunnel, band: TenureBand): FunnelView {
  const bandSteps = funnel.targets[band].steps;
  const cohort = funnel.cohort.patients;
  const medicareCapped = medicareCappedOf(funnel.steps);
  const s3 = funnel.steps.find((step) => step.id === "s3");
  const nonMedicare = (s3?.patients ?? 0) - medicareCapped;
  const steps = funnel.steps.map((step) => ({
    id: step.id,
    label: step.label,
    sublabel: stepSublabel(step, bandSteps[step.id], nonMedicare),
    patients: step.patients,
    sharePercent: percentOf(step.patients, cohort),
    medicareSharePercent: percentOf(step.medicareCapped ?? 0, cohort),
    status: bandSteps[step.id].status,
  }));
  const gaps = funnel.steps.slice(0, -1).map((step, index) => {
    const next = funnel.steps[index + 1] as FunnelStep;
    const base = step.id === "s3" ? nonMedicare : step.patients;

    return {
      afterStepId: step.id,
      dropOffPercent: percentOf(base - next.patients, base),
      targetText: funnelTargetText(bandSteps[next.id].target),
      status: bandSteps[next.id].status,
    };
  });

  return { steps, gaps, medicareCapped };
}

export function funnelGapText(gap: FunnelGapView): string {
  return `Drop-off ${gap.dropOffPercent}% · ${gap.targetText}`;
}
